// User-tunable appearance overrides. Light/dark mode is owned by next-themes;
// these CSS-variable overrides ride on top so user picks survive mode toggles.
// Persistence is mixed:
//   - localStorage for instant first paint on this device.
//   - UserDisplay row (per-user, cross-device) for accent + background.
//   - localStorage-only for statusColors. Adding eight DB columns just for
//     per-status tints isn't worth the migration; a re-pick on a new device
//     is acceptable.

import {
  bgVar,
  deriveForeground,
  fgVar,
  STATUS_DEFINITIONS,
  STATUS_KEYS,
  type StatusKey,
} from "@/lib/status-colors";

export type StatusColorOverrides = Partial<Record<StatusKey, string>>;

// Loss-ratio bands: text color shown on the Loss Ratio detail page (and any
// future page that surfaces a percent). Three bands separated by two
// thresholds — below low, between, above high. Both thresholds and band
// colors are user-tunable. Stored as part of Appearance so a single picker
// owns them.
export type LossRatioAppearance = {
  lowThreshold: number;
  highThreshold: number;
  lowColor: string;
  midColor: string;
  highColor: string;
};

export const LOSS_RATIO_DEFAULTS: LossRatioAppearance = {
  lowThreshold: 45,
  highThreshold: 65,
  lowColor: "#15803d", // green-700
  midColor: "#d97706", // amber-600
  highColor: "#dc2626", // red-600
};

export type Appearance = {
  // Accent / "app blue" — drives panel headers, primary buttons, links, the
  // top nav bar background. Leave null to fall back to MEMIC defaults.
  accent: string | null;
  // App page background (NOT the white card/panel background). Leave null
  // to fall back to MEMIC defaults.
  background: string | null;
  // Per-status background-color overrides. Missing keys fall back to the
  // defaults in STATUS_DEFINITIONS.
  statusColors: StatusColorOverrides;
  // Loss-ratio band thresholds + colors. Always present (no per-field null);
  // a "reset" restores LOSS_RATIO_DEFAULTS.
  lossRatio: LossRatioAppearance;
};

export const APPEARANCE_DEFAULTS: Appearance = {
  accent: null,
  background: null,
  statusColors: {},
  lossRatio: LOSS_RATIO_DEFAULTS,
};

const STORAGE_KEY = "memic-appearance";

export function loadAppearance(): Appearance {
  if (typeof window === "undefined") return APPEARANCE_DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return APPEARANCE_DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Appearance>;
    return {
      accent: typeof parsed.accent === "string" ? parsed.accent : null,
      background: typeof parsed.background === "string" ? parsed.background : null,
      statusColors: sanitizeStatusColors(parsed.statusColors),
      lossRatio: sanitizeLossRatio(parsed.lossRatio),
    };
  } catch {
    return APPEARANCE_DEFAULTS;
  }
}

export function saveAppearance(value: Appearance): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

// Apply (or unset) CSS variable overrides on the root element. Setting an
// empty string removes the inline override and lets the stylesheet defaults
// take over.
//
// Accent overrides --primary + --ring (the "blue" used by panel headers,
// links, focus rings). Background is set directly on <body> rather than
// overriding the --background CSS variable — that variable is also read by
// outline buttons and other surfaces, so changing it here leaks the page
// color into UI primitives.
//
// Status colors: for every StatusKey we write a paired --status-<key>-bg /
// --status-<key>-fg variable. The fg is auto-derived from bg luminance so
// the user only picks one swatch per status. We always write *both* (override
// or default) so the StatusBadge component never needs a fallback path.
export function applyAppearance(value: Appearance, isDark = false): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (value.accent) {
    root.style.setProperty("--primary", value.accent);
    root.style.setProperty("--ring", value.accent);
  } else {
    root.style.removeProperty("--primary");
    root.style.removeProperty("--ring");
  }
  if (document.body) {
    // Background pick is treated as a light-mode customization. In dark
    // mode we suspend the inline override so the default dark surface
    // shows through — otherwise a cream/pastel pick "leaks" into dark
    // mode and fights the rest of the dark UI.
    document.body.style.backgroundColor = isDark ? "" : (value.background ?? "");
  }
  // Mirror body bg to --page-bg so sticky page-level regions can match
  // the user's pick; callers fall back to var(--background) when unset.
  if (isDark || !value.background) {
    root.style.removeProperty("--page-bg");
  } else {
    root.style.setProperty("--page-bg", value.background);
  }

  for (const key of STATUS_KEYS) {
    const bg = value.statusColors[key] ?? STATUS_DEFINITIONS[key].defaultBg;
    root.style.setProperty(bgVar(key), bg);
    root.style.setProperty(fgVar(key), deriveForeground(bg));
  }

  root.style.setProperty("--loss-ratio-low", value.lossRatio.lowColor);
  root.style.setProperty("--loss-ratio-mid", value.lossRatio.midColor);
  root.style.setProperty("--loss-ratio-high", value.lossRatio.highColor);
}

// Pick the band color for a given loss-ratio percentage. 0% (legitimate
// "no losses" against earned premium) lands in the low band — green by
// default — so a clean policy reads visibly as green rather than as muted
// "no data". Callers that want to distinguish "no premium / no data"
// should gate on earned premium themselves (see `LossRatioValue` /
// `SummaryCard`) and render an em-dash instead of calling this.
export function lossRatioColor(value: number, bands: LossRatioAppearance): string {
  if (value >= bands.highThreshold) return bands.highColor;
  if (value >= bands.lowThreshold) return bands.midColor;
  return bands.lowColor;
}

function sanitizeStatusColors(raw: unknown): StatusColorOverrides {
  if (!raw || typeof raw !== "object") return {};
  const out: StatusColorOverrides = {};
  for (const key of STATUS_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value)) {
      out[key] = value;
    }
  }
  return out;
}

// Bounded read: thresholds clamped to 0-1000 so a corrupt localStorage value
// can't break the band math. lowThreshold must be < highThreshold; if the
// stored pair is inverted or equal, fall back to defaults for both.
function sanitizeLossRatio(raw: unknown): LossRatioAppearance {
  if (!raw || typeof raw !== "object") return LOSS_RATIO_DEFAULTS;
  const r = raw as Record<string, unknown>;
  const pickNumber = (v: unknown, fallback: number): number => {
    if (typeof v !== "number" || !Number.isFinite(v)) return fallback;
    return Math.min(1000, Math.max(0, v));
  };
  const pickHex = (v: unknown, fallback: string): string =>
    typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v) ? v : fallback;
  const low = pickNumber(r.lowThreshold, LOSS_RATIO_DEFAULTS.lowThreshold);
  const high = pickNumber(r.highThreshold, LOSS_RATIO_DEFAULTS.highThreshold);
  const ordered = low < high;
  return {
    lowThreshold: ordered ? low : LOSS_RATIO_DEFAULTS.lowThreshold,
    highThreshold: ordered ? high : LOSS_RATIO_DEFAULTS.highThreshold,
    lowColor: pickHex(r.lowColor, LOSS_RATIO_DEFAULTS.lowColor),
    midColor: pickHex(r.midColor, LOSS_RATIO_DEFAULTS.midColor),
    highColor: pickHex(r.highColor, LOSS_RATIO_DEFAULTS.highColor),
  };
}
